<template>
  <div class="new-password-form">
    <WaveBg />

    <!-- Password Entry Form -->
    <div class="form-centered password-reset-form" v-if="!resetComplete">
      <h1>Reset Your Password</h1>
      <p class="recipient-email">{{ email }}</p>

      <div class="form-row">
        <FormInput
          type="text"
          label="New Password"
          name="newPass"
          v-model.trim="v$.newPass.$model"
          :valid="!v$.newPass.$error"
        />
        <p class="subtext">At least eight characters with a lower case letter, Upper case letter, and a number</p>
      </div>

      <div class="form-row">
        <FormInput
          type="text"
          label="Retype New Password"
          v-model.trim="v$.newPassConfirm.$model"
          :valid="!v$.newPassConfirm.$error"
        />

        <p class="password-mismatch-text">
          {{ mismatch ? 'Passwords do not match' : '&nbsp;' }}
        </p>
      </div>

      <div class="form-button">
        <button type="button" class="reset-button" :class="{ disabled: disabled }" @click="resetPass">
          Reset Password
        </button>
        <p class="reset-error">
          {{ resetErrorMessage ? resetErrorMessage : '&nbsp;' }}
        </p>
      </div>
    </div>

    <!-- Password Reset Success -->
    <div class="form-centered password-reset-success" v-else>
      <h1>Your Password Has Been Changed</h1>
      <p>Your password has been successfully reset. Please log back into the HUB using your new password.</p>
      <router-link class="reset-button" :to="{ name: 'login' }">Go to HUB log In</router-link>
    </div>

    <HUBLoader v-if="submitting" />
  </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';

import WaveBg from '@/components/common/WaveBackground.vue';
import HUBLoader from '@/components/common/hubLoading.vue';
import FormInput from '@/components/form/FormInput.vue';

export default {
  name: 'NewPassword',

  setup() {
    return {
      v$: useVuelidate(),
    };
  },

  data() {
    return {
      // Recipient's Email
      email: '',
      token: '',
      newPass: '',
      newPassConfirm: '',
      resetErrorMessage: '',
      resetComplete: false,
      submitting: false,
    };
  },

  validations: {
    newPass: {
      required,
      isPasswordValid(value) {
        const passRegex = /^(?=.*[0-9])(?=.*[A-z])(?=.*[!@#$%^&*()[\]\\/'":;,.?-_=?]).{8,}$/;
        return passRegex.test(value);
      },
    },
    newPassConfirm: {
      required,
      isPasswordValid(value) {
        const passRegex = /^(?=.*[0-9])(?=.*[A-z])(?=.*[!@#$%^&*()[\]\\/'":;,.?-_=?]).{8,}$/;
        return passRegex.test(value);
      },
      isSameAsPass(value) {
        return value === this.newPass;
      },
    },
  },

  async created() {
    if (!this.$route.params.token) {
      this.$router.push({ name: 'login' });
      return;
    }

    try {
      await this.$api.get(`/password/link?token=${this.$route.params.token}`);
      this.token = this.$route.params.token;
      this.email = JSON.parse(atob(this.token.split('.')[1])).email;
    } catch (error) {
      // TODO: maybe a warning page
      // console.log(error.message);
      this.$router.push({ name: 'login' });
    }
  },

  methods: {
    async resetPass() {
      if (this.disabled) return;

      this.resetErrorMessage = '';
      this.submitting = true;

      try {
        await this.$api.post('/password', {
          token: this.token,
          pass: this.newPass,
        });

        this.submitting = false;
        this.resetComplete = true;
      } catch (error) {
        this.submitting = false;
        this.resetErrorMessage =
          error.message || 'There was a problem resetting your password. Please try again later.';
      }
    },
  },

  computed: {
    mismatch() {
      return this.newPassConfirm.length > 0 && !this.v$.newPassConfirm.isSameAsPass;
    },

    disabled() {
      return !this.newPass || !this.newPassConfirm || this.v$.$invalid || this.mismatch || this.submitting;
    },
  },

  components: {
    WaveBg,
    HUBLoader,
    FormInput,
  },
};
</script>

<style scoped>
.new-password-form {
  height: 100%;
  width: 100%;
}

.form-centered {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  position: relative;
  box-sizing: border-box;
  width: 100%;
  min-height: 100%;
  padding: 2em 1.5em;
  font-size: 1.1em;
  text-align: center;
}

h1,
p,
a {
  margin: 0;
  padding: 0;
  color: #fff;
  font-weight: 400;
}

.recipient-email {
  height: 1.1em;
  line-height: 1.1em;
  font-size: 1.1em;
}

.form-row {
  max-width: 25em;
  width: 100%;
}
.subtext,
.password-mismatch-text {
  margin-top: 0.2em;
  font-size: 0.9em;
  font-style: italic;
  text-align: center;
}

/**
 * Custom form input
 */
.password-reset-form :deep(.form-input) {
  display: flex;
  justify-content: center;
}
/** input label */
.password-reset-form :deep(.form-input).focused label,
.password-reset-form :deep(.form-input).filled label {
  bottom: 0.2em;
  font-size: 1.1em;
  opacity: 0;
}
.password-reset-form :deep(.form-input) label {
  color: #fff;
}
/** input box */
.password-reset-form :deep(.form-input) input {
  border-bottom: 1px solid #fff;
  color: #fff;
  text-align: center;
}
.password-reset-form :deep(.form-input):not(.focused).filled input {
  color: #fff;
}
.password-reset-form :deep(.form-input):not(.focused).error input {
  color: #ffc4c9;
}
/**
 * End Custom form input
 */

.form-button {
  position: relative;
  margin-top: 3em;
}
.reset-button {
  display: flex;
  align-items: center;
  justify-content: center;

  border: 0 none;
  border-radius: 0.5em;
  padding: 0.5em 4em;
  background: rgba(255, 255, 255, 0.26);
  color: #fff;
  font-size: 1em;
  text-decoration: none;
  cursor: pointer;
  transition: all ease-in-out 0.1s;
}
.reset-button:not(.disabled):hover {
  box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.3);
  background: rgba(255, 255, 255, 0.4);
}

.reset-button.disabled {
  cursor: default;
  pointer-events: none;
}

.reset-error {
  position: absolute;
  left: 50%;
  bottom: 3em;
  transform: translate(-50%, 0);
  color: #ffc4c9;
  text-align: center;
}

.password-reset-success p {
  margin: 3em 0;
  max-width: 30em;
}
</style>
